export interface WebSocketHandlerOptions<T = any> {
    protocols?: string | string[];
    timeout?: number;
    maxAttempts?: number;
    autoReconnect?: boolean;
    heartbeatInterval?: number;
    getHeartbeatPayload?: () => any;
    onOpen?: (event: Event) => void;
    onMessage?: (event: MessageEvent<T>) => void;
    onReconnect?: (event: Event | CloseEvent) => void;
    onMaxAttemptsReached?: (event: Event | CloseEvent) => void;
    onClose?: (event: CloseEvent) => void;
    onError?: (event: Event) => void;
}

// https://datatracker.ietf.org/doc/html/rfc6455#section-7.4.1
export enum WebSocketEventCode {
    NormalClosure = 1000,
    GoingAway = 1001,
    ProtocolError = 1002,
    UnsupportedData = 1003,
    // 1004 is Reserved
    NoStatusReceived = 1005, // Reserved: Internal use only
    AbnormalClosure = 1006, // Reserved: Internal use only
    InvalidFramePayload = 1007,
    PolicyViolation = 1008,
    MessageTooBig = 1009,
    MandatoryExtension = 1010,
    InternalServerError = 1011,
    ServiceRestart = 1012,
    TryAgainLater = 1013,
    BadGateway = 1014,
    TlsHandshakeFailed = 1015, // Reserved: Internal use only
}

export enum WebSocketStatus {
    Connecting = WebSocket.CONNECTING,
    Open = WebSocket.OPEN,
    Closing = WebSocket.CLOSING,
    Closed = WebSocket.CLOSED,
}

export class WebSocketHandler<T = any> {
    private url: string;
    private options: WebSocketHandlerOptions<T>;
    private webSocket: WebSocket;
    private attempts = 0;
    private reconnectTimer = 0;
    private heartbeatTimer = 0;
    private pending: any[] = [];
    private isExplicitlyClosed = false;

    constructor(url: string, options: WebSocketHandlerOptions<T> = {}) {
        this.url = url;
        this.options = options;
        this.webSocket = this.open();
    }

    private open(): WebSocket {
        const { protocols, onClose, onError, onMessage, onOpen, autoReconnect } = this.options;

        const webSocket = new WebSocket(this.url, protocols);

        webSocket.onmessage = onMessage || null;
        webSocket.onopen = (event) => {
            onOpen?.(event);
            this.attempts = 0;
            this.isExplicitlyClosed = false;

            while (this.pending.length > 0) {
                this.send(this.pending.shift());
            }

            this.startHeartbeat();
        };
        webSocket.onclose = (event) => {
            this.stopHeartbeat();
            this.stopReconnect();
            onClose?.(event);

            const shouldReconnect = !this.isExplicitlyClosed && autoReconnect;
            if (shouldReconnect) {
                this.reconnect(event);
            }
        };
        webSocket.onerror = (event) => {
            if (autoReconnect) {
                this.reconnect(event);
            } else {
                onError?.(event);
            }
        };

        return webSocket;
    }

    private startHeartbeat() {
        const { heartbeatInterval, getHeartbeatPayload } = this.options;
        if (!heartbeatInterval || !getHeartbeatPayload) {
            return;
        }

        this.stopHeartbeat();
        this.heartbeatTimer = window.setInterval(() => {
            if (this.isOpen) {
                const payload = getHeartbeatPayload();
                if (typeof payload === 'string') {
                    this.send(payload);
                } else {
                    this.json(payload);
                }
            }
        }, heartbeatInterval);
    }

    private stopHeartbeat() {
        window.clearInterval(this.heartbeatTimer);
    }

    private reconnect(event: Event | CloseEvent) {
        const { maxAttempts = 0, timeout, onReconnect, onMaxAttemptsReached } = this.options;

        if (this.attempts++ < maxAttempts) {
            this.reconnectTimer = window.setTimeout(() => {
                onReconnect?.(event);
                this.webSocket = this.open();
            }, timeout ?? 1000);
        } else {
            onMaxAttemptsReached?.(event);
        }
    }

    private stopReconnect() {
        window.clearTimeout(this.reconnectTimer);
    }

    json(payload: any) {
        this.send(JSON.stringify(payload));
    }

    send(payload: any) {
        if (this.isOpen) {
            this.webSocket.send(payload);
        } else {
            this.pending.push(payload);
        }
    }

    close(code?: number, reason?: string) {
        this.isExplicitlyClosed = true;
        this.webSocket.close(code || WebSocketEventCode.NormalClosure, reason);
    }

    updateUrl(url: string) {
        this.url = url;
    }

    getWebSocket(): WebSocket {
        return this.webSocket;
    }

    get status() {
        return this.webSocket.readyState as WebSocketStatus;
    }

    get isOpen() {
        return this.status === WebSocketStatus.Open;
    }
}
